import { Product, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import type { RootStackParamList } from '@/navigation/types';
import { create } from 'zustand';

export type ProductTransitionTargetScreen = { name: keyof RootStackParamList; params?: object };

type ProductTransitionActions = {
    triggerTransition: (product: Product, targetScreen?: ProductTransitionTargetScreen) => void;
    completeTransition: () => void;
};

type ProductTransitionState = {
    isPending: boolean;
    product: Product;
    previousProduct: Product;
    targetScreen?: ProductTransitionTargetScreen;
    actions: ProductTransitionActions;
};

/**
 * Drives the product-switch overlay (ProductTransitionOverlay).
 *
 * - any callsite (tab press, deep link, product-switcher modal) calls triggerTransition
 * - isPending flips to true, which mounts the overlay and starts the fade-in
 * - once faded in, the overlay resets the nav stack to the new product lobby and sets the product
 * - the overlay fades out, navigates to targetScreen if given, then calls completeTransition
 * - isPending flips to false, which unmounts the overlay
 *
 * The overlay lives outside the navigator, so one reset is enough. The old
 * ProductTransitionScreen was a real screen and needed two to avoid flickering.
 */
export const useProductTransitionStore = create<ProductTransitionState>((set, get) => ({
    isPending: false,
    product: Product.Pickem,
    previousProduct: Product.Pickem,
    targetScreen: undefined,
    actions: {
        // ignore re-entrant calls while a transition is already in flight
        triggerTransition: (product, targetScreen) => {
            if (get().isPending) {
                return;
            }

            set({
                isPending: true,
                previousProduct: useJurisdictionStore.getState().product,
                product,
                targetScreen,
            });
        },
        completeTransition: () =>
            set({
                isPending: false,
                targetScreen: undefined,
            }),
    },
}));
